import { ReactElement, useRef, useEffect, useState } from 'react';
import Plotly from 'plotly.js-dist-min';
import Spinner from '../Spinner';
import dashboardIcon from '../../icons/logo-streamlines.png';

interface ScatterDataPoint {
  x: number; // pressure value
  y: number; // delta value
  sessionName: string;
  surface?: string; // Used when groupBy is 'tyre'
  tyreName?: string; // Used when groupBy is 'surface'
  surfaceValue?: number; // Numeric surface quality for sorting
  isPrime?: boolean; // First config at this (tyre, pressure) per session
}

interface TyreDeltaScatterChartProps {
  // Session info
  sessionDate?: string;
  sessionDescription?: string;
  airspeedKph?: number;

  // Baseline info
  baselineTyreName?: string;
  baselinePressure?: number;

  // Plot data - organized by tyre type or surface type
  scatterData: {
    [key: string]: ScatterDataPoint[];
  };

  // Plot options
  pressureUnit: 'Bar' | 'PSI';
  deltaAverage: 'Constant' | 'Moving';
  groupBy?: 'tyre' | 'surface'; // How the data is grouped

  // Equipment catalog for sorting
  equipmentCatalog?: any;

  // Optional styling
  height?: number;
  showLogo?: boolean;

  // Customisation for different value types
  xAxisLabel?: string; // Override x-axis title
  xAxisType?: 'linear' | 'log'; // X-axis scale type
  yAxisLabel?: string; // Override y-axis title
  plotTitleMain?: string; // Override main title text
  plotTitleSub?: string; // Override subtitle text
  lineFromPrimeOnly?: boolean; // Only draw line through prime configs
  showLines?: boolean; // Whether to draw connecting lines (default true)
  hoverYLabel?: string; // Label for y value in hover
  hoverYFormat?: string; // Format for y value in hover
  hoverXLabel?: string; // Label for x value in hover
  hoverXFormat?: string; // Format for x value in hover
}

// Available marker symbols to cycle through
const AVAILABLE_MARKERS = [
  'circle',
  'square',
  'diamond',
  'cross',
  'triangle-up',
  'triangle-down',
  'pentagon',
  'hexagon',
  'star',
  'x',
];

// Available colors to cycle through
const AVAILABLE_COLORS = [
  '#1f77b4', // blue
  '#ff7f0e', // orange
  '#2ca02c', // green
  '#d62728', // red
  '#9467bd', // purple
  '#8c564b', // brown
  '#e377c2', // pink
  '#7f7f7f', // gray
  '#bcbd22', // olive
  '#17becf', // cyan
];

// Helper function to find tyre width for sorting, handles combined "Front / Rear" names
const getTyreWidthForSorting = (tyreName: string, equipmentCatalog: any): number => {
  if (!equipmentCatalog || !equipmentCatalog.tyres) {
    return Infinity;
  }

  // Check if this is a combined tyre name (contains " / ")
  let searchName = tyreName;
  if (tyreName.includes(' / ')) {
    // Extract the rear tyre name (after the " / ")
    searchName = tyreName.split(' / ')[1];
  }

  // Find the tyre in the catalog
  const tyre = equipmentCatalog.tyres.find(
    (t: any) => t.name === searchName || t.designation === searchName || `Tyre ${t.id.slice(0, 8)}` === searchName,
  );

  return tyre?.width_mm || Infinity;
};

export default function TyreDeltaScatterChart({
  sessionDate,
  sessionDescription,
  airspeedKph,
  baselineTyreName = '',
  baselinePressure = 0,
  scatterData,
  pressureUnit,
  deltaAverage,
  groupBy = 'tyre',
  equipmentCatalog,
  height = 600,
  showLogo = true,
  xAxisLabel,
  xAxisType,
  yAxisLabel,
  plotTitleMain,
  plotTitleSub,
  lineFromPrimeOnly = false,
  showLines = true,
  hoverYLabel = 'Delta Power',
  hoverYFormat = '.3f',
  hoverXLabel,
  hoverXFormat,
}: TyreDeltaScatterChartProps): ReactElement {
  const plotRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Calculate if we need 2 rows
  const groupKeys = Object.keys(scatterData);
  const numPlots = groupKeys.length;
  const needsTwoRows = numPlots > 3;
  // Increase height by 80% when using two rows
  const effectiveHeight = needsTwoRows ? Math.round(height * 1.8) : height;

  useEffect(() => {
    if (plotRef.current) {
      setIsLoading(true);

      const groupKeys = Object.keys(scatterData);

      if (groupKeys.length > 0) {
        // Collect all unique secondary grouping types (surface or tyre) across all data
        const allSecondaryKeys = new Set<string>();
        const keyToSurfaceValue: { [key: string]: number } = {}; // Map keys to surface values for sorting

        Object.values(scatterData).forEach((points) => {
          points.forEach((point) => {
            const key = groupBy === 'tyre' ? point.surface || 'Unknown' : point.tyreName || 'Unknown';
            allSecondaryKeys.add(key);

            // Store the surface value for this key
            if (point.surfaceValue !== undefined && !keyToSurfaceValue[key]) {
              keyToSurfaceValue[key] = point.surfaceValue;
            }
          });
        });

        // Create mapping of secondary keys to markers and colors
        // Sort legend by surface value (when groupBy='tyre') or by tyre width (when groupBy='surface')
        const keyToMarker: { [key: string]: string } = {};
        const keyToColor: { [key: string]: string } = {};
        const sortedSecondaryKeys = Array.from(allSecondaryKeys).sort((a, b) => {
          if (groupBy === 'tyre') {
            // When groupBy is 'tyre', secondary keys are surfaces - sort by surface value
            const surfaceValueA = keyToSurfaceValue[a];
            const surfaceValueB = keyToSurfaceValue[b];

            if (surfaceValueA !== undefined && surfaceValueB !== undefined) {
              return surfaceValueA - surfaceValueB;
            }
            return a.localeCompare(b);
          } else {
            // When groupBy is 'surface', secondary keys are tyres - sort by tyre width
            const widthA = getTyreWidthForSorting(a, equipmentCatalog);
            const widthB = getTyreWidthForSorting(b, equipmentCatalog);
            return widthA - widthB;
          }
        });

        sortedSecondaryKeys.forEach((key, index) => {
          keyToMarker[key] = AVAILABLE_MARKERS[index % AVAILABLE_MARKERS.length];
          keyToColor[key] = AVAILABLE_COLORS[index % AVAILABLE_COLORS.length];
        });

        // Sort group keys
        const sortedGroupKeys =
          groupBy === 'tyre'
            ? groupKeys.sort((a, b) => {
                // When groupBy is 'tyre', sort by width
                const widthA = getTyreWidthForSorting(a, equipmentCatalog);
                const widthB = getTyreWidthForSorting(b, equipmentCatalog);
                return widthA - widthB;
              })
            : groupKeys.sort((a, b) => {
                // When groupBy is 'surface', sort by numeric surface value (low to high)
                const surfaceValueA = scatterData[a]?.[0]?.surfaceValue ?? 999;
                const surfaceValueB = scatterData[b]?.[0]?.surfaceValue ?? 999;
                return surfaceValueA - surfaceValueB;
              });

        // Calculate global y-axis range
        let globalMin = Infinity;
        let globalMax = -Infinity;

        Object.values(scatterData).forEach((points) => {
          points.forEach((point) => {
            if (point.y != null && isFinite(point.y)) {
              globalMin = Math.min(globalMin, point.y);
              globalMax = Math.max(globalMax, point.y);
            }
          });
        });

        // For delta plots, include zero; for absolute plots, just fit data
        if (!yAxisLabel && globalMin >= 0) {
          globalMin = -2;
        }

        const range = globalMax - globalMin;
        const padding = range * 0.1;
        globalMin -= padding;
        globalMax += padding;

        // Prepare traces for each group (tyre or surface)
        const traces: any[] = [];
        const numPlots = sortedGroupKeys.length;
        // Use 2 rows if more than 3 plots, otherwise single row
        const rows = numPlots > 3 ? 2 : 1;
        const cols = rows === 2 ? Math.ceil(numPlots / 2) : numPlots;

        // First, add all legend-only traces in sorted order
        sortedSecondaryKeys.forEach((secondaryKey) => {
          const baseColor = keyToColor[secondaryKey] || '#000000';
          traces.push({
            x: [null],
            y: [null],
            type: 'scatter',
            mode: 'markers',
            name: secondaryKey,
            legendgroup: secondaryKey,
            marker: {
              symbol: keyToMarker[secondaryKey] || 'circle',
              size: 10,
              color: baseColor,
              opacity: 1.0, // Full opacity for legend
              line: {
                color: '#333',
                width: 1,
              },
            },
            showlegend: true,
            hoverinfo: 'skip',
          });
        });

        // Now add all data traces (markers and lines)
        sortedGroupKeys.forEach((groupKey, index) => {
          const groupPoints = scatterData[groupKey];

          // Group points by secondary key (surface if groupBy is 'tyre', tyre if groupBy is 'surface')
          const secondaryGroups: { [key: string]: ScatterDataPoint[] } = {};
          groupPoints.forEach((point) => {
            const secondaryKey = groupBy === 'tyre' ? point.surface || 'Unknown' : point.tyreName || 'Unknown';
            if (!secondaryGroups[secondaryKey]) {
              secondaryGroups[secondaryKey] = [];
            }
            secondaryGroups[secondaryKey].push(point);
          });

          // Create a trace for each secondary group
          Object.entries(secondaryGroups).forEach(([secondaryKey, points]) => {
            // Sort points by x value (pressure)
            const sortedPoints = [...points].sort((a, b) => a.x - b.x);

            // Calculate averages for each unique pressure value
            // Round pressures before grouping (0.01 bar or 0.1 psi)
            // When lineFromPrimeOnly, only prime configs contribute to the line
            const linePoints = lineFromPrimeOnly
              ? sortedPoints.filter((p) => p.isPrime !== false)
              : sortedPoints;
            const pressureMap: {
              [pressure: number]: { sum: number; count: number };
            } = {};
            const roundingFactor = pressureUnit === 'PSI' ? 10 : 100;
            linePoints.forEach((point) => {
              const roundedPressure =
                Math.round(point.x * roundingFactor) / roundingFactor;
              if (!pressureMap[roundedPressure]) {
                pressureMap[roundedPressure] = { sum: 0, count: 0 };
              }
              pressureMap[roundedPressure].sum += point.y;
              pressureMap[roundedPressure].count += 1;
            });

            // Create average points
            const averagePoints = Object.keys(pressureMap)
              .map(Number)
              .sort((a, b) => a - b)
              .map((pressure) => ({
                x: pressure,
                y: pressureMap[pressure].sum / pressureMap[pressure].count,
              }));

            // Add scatter trace for individual points
            const baseColor = keyToColor[secondaryKey] || '#000000';

            // Add the actual data points with reduced opacity (not shown in legend)
            traces.push({
              x: sortedPoints.map((p) => p.x),
              y: sortedPoints.map((p) => p.y),
              type: 'scatter',
              mode: 'markers',
              name: secondaryKey,
              legendgroup: secondaryKey,
              xaxis: `x${index + 1}`,
              yaxis: `y${index + 1}`,
              marker: {
                symbol: keyToMarker[secondaryKey] || 'circle',
                size: 10,
                color: baseColor,
                opacity: 0.4, // 40% opacity for data points
                line: {
                  color: '#333',
                  width: 1,
                },
              },
              text: sortedPoints.map((p) => p.sessionName),
              hovertemplate:
                '<b>' + groupKey + '</b><br>' +
                'Session: %{text}<br>' +
                (hoverXLabel || 'Pressure') +
                ': %{x:' + (hoverXFormat || '.1f') + '}' +
                '<br>' +
                hoverYLabel + ': %{y:' + hoverYFormat + '}<br>' +
                (groupBy === 'tyre'
                  ? 'Surface: ' + secondaryKey
                  : 'Tyre: ' + secondaryKey) +
                '<br><extra></extra>',
              showlegend: false,
            });

            // Add line trace for averages (if enabled)
            if (showLines) {
              traces.push({
                x: averagePoints.map((p) => p.x),
                y: averagePoints.map((p) => p.y),
                type: 'scatter',
                mode: 'lines',
                name: secondaryKey,
                legendgroup: secondaryKey,
                xaxis: `x${index + 1}`,
                yaxis: `y${index + 1}`,
                line: {
                  color: keyToColor[secondaryKey] || '#000000',
                  width: 3,
                },
                hovertemplate:
                  '<b>' + groupKey + '</b><br>' +
                  'Avg ' + hoverYLabel +
                  ': %{y:' + hoverYFormat + '}<br>' +
                  (hoverXLabel || 'Pressure') +
                  ': %{x:' + (hoverXFormat || '.1f') +
                  '}<br>' +
                  (groupBy === 'tyre'
                    ? 'Surface: ' + secondaryKey
                    : 'Tyre: ' + secondaryKey) +
                  '<br><extra></extra>',
                showlegend: false,
              });
            }
          });
        });

        // Create subplot layout
        // Build title text
        const groupLabel = groupBy === 'tyre'
          ? 'Tyre Type' : 'Surface';
        const defaultTitle =
          `Power Delta by ${groupLabel} and Pressure` +
          ` - All Sessions (${deltaAverage} Average)`;
        const defaultSub =
          `Compared to ${baselineTyreName} at ` +
          `${baselinePressure}${pressureUnit.toLowerCase()}` +
          ` at ${airspeedKph || 0} kph - ` +
          (deltaAverage === 'Constant'
            ? 'Averaged Baseline' : 'Nearest Pair Baseline');
        const titleMain = plotTitleMain ?? defaultTitle;
        const titleSub = plotTitleSub ?? defaultSub;
        const fullTitle = titleMain + '<br>' +
          '<span style="font-size: 12px; font-weight: normal;' +
          ' color: #666;">' + titleSub + '</span>';

        const layout: any = {
          title: {
            text: fullTitle,
            font: { family: 'Poppins', size: 16 },
          },
          showlegend: true,
          legend: {
            orientation: 'h',
            x: 0.5,
            xanchor: 'center',
            y: -0.20,
            yanchor: 'top',
            font: { family: 'Poppins', size: 11 },
            itemsizing: 'constant',
            tracegroupgap: 5,
            entrywidth: 0.3,
            entrywidthmode: 'fraction',
          },
          margin: {
            l: 120,
            r: 40,
            t: 140,
            b: 180,
          },
          hovermode: 'closest',
          plot_bgcolor: '#f8f9fa',
          paper_bgcolor: 'white',
          font: { family: 'Poppins' },
        };

        // Add annotations for subplot titles
        layout.annotations = sortedGroupKeys.map((groupKey, index) => {
          const row = Math.floor(index / cols);
          const col = index % cols;
          // Calculate y position based on row (top row is higher)
          // Match the rowGap used in axis configuration (0.18)
          const rowTop = rows === 2 ? (row === 0 ? 1.02 : 0.42) : 1.02;
          return {
            text: groupKey,
            x: (col + 0.5) / cols,
            y: rowTop,
            xref: 'paper',
            yref: 'paper',
            xanchor: 'center',
            yanchor: 'bottom',
            font: { family: 'Poppins', size: 14, weight: 'bold' },
            showarrow: false,
          };
        });

        // Add logo image in top left margin if enabled
        if (showLogo) {
          layout.images = [
            {
              source: dashboardIcon,
              xref: 'paper',
              yref: 'paper',
              x: -0.0,
              y: 1.25,
              sizex: 0.3,
              sizey: 0.15,
              xanchor: 'left',
              yanchor: 'bottom',
              opacity: 0.8,
              layer: 'above',
            },
          ];
        }

        // Configure subplot axes
        sortedGroupKeys.forEach((_, index) => {
          const axisNum = index + 1;
          const row = Math.floor(index / cols);
          const col = index % cols;

          // Calculate domains for multi-row layout
          const rowGap = 0.18; // Gap between rows (increased for better separation)
          const colGap = 0.07; // Gap between columns
          const rowHeight = rows === 2 ? (1 - rowGap) / 2 : 1;
          const colWidth = (1 - colGap * (cols - 1)) / cols;

          // X domain: column position
          const xStart = col * (colWidth + colGap);
          const xEnd = xStart + colWidth;

          // Y domain: row position (row 0 is top, so higher y values)
          const yEnd = rows === 2 ? (row === 0 ? 1 : 0.41 - rowGap / 2) : 1;
          const yStart = yEnd - rowHeight;

          // X-axis configuration
          const defaultXLabel =
            `Tyre Pressure (${pressureUnit.toLowerCase()})`;
          layout[`xaxis${axisNum === 1 ? '' : axisNum}`] = {
            title: {
              text: xAxisLabel ?? defaultXLabel,
              font: { family: 'Poppins' },
            },
            type: xAxisType || 'linear',
            showgrid: true,
            gridcolor: '#e0e0e0',
            domain: [xStart + 0.02, xEnd - 0.02],
            anchor: `y${axisNum}`,
          };

          // Y-axis configuration - show title only on leftmost plot of each row
          const defaultYLabel =
            'Increased Rolling Resistance<br>(Watts)';
          layout[`yaxis${axisNum === 1 ? '' : axisNum}`] = {
            title: {
              text: col === 0 ? (yAxisLabel ?? defaultYLabel) : '',
              font: { family: 'Poppins' },
            },
            zeroline: true,
            zerolinewidth: 2,
            zerolinecolor: 'gray',
            showgrid: true,
            gridcolor: '#e0e0e0',
            range: [globalMin, globalMax],
            domain: [yStart, yEnd],
            anchor: `x${axisNum}`,
          };
        });

        const config = {
          displayModeBar: true,
          modeBarButtonsToRemove: ['lasso2d', 'select2d'],
          displaylogo: false,
          responsive: true,
        };

        Plotly.newPlot(plotRef.current, traces, layout, config).then(() => {
          setIsLoading(false);
        });
      } else {
        // Clear the plot if no data
        Plotly.purge(plotRef.current);
        setIsLoading(false);
      }
    }
  }, [
    scatterData,
    pressureUnit,
    deltaAverage,
    groupBy,
    equipmentCatalog,
    sessionDate,
    sessionDescription,
    airspeedKph,
    baselineTyreName,
    baselinePressure,
    height,
    showLogo,
    xAxisLabel,
    xAxisType,
    yAxisLabel,
    plotTitleMain,
    plotTitleSub,
    lineFromPrimeOnly,
    showLines,
    hoverYLabel,
    hoverYFormat,
    hoverXLabel,
    hoverXFormat,
  ]);

  return (
    <div className="cr-relative cr-w-full" style={{ height: `${effectiveHeight}px` }}>
      {isLoading && (
        <div className="cr-absolute cr-inset-0 cr-z-10 cr-flex cr-items-center cr-justify-center cr-bg-white cr-bg-opacity-75">
          <Spinner isFetching={true} />
        </div>
      )}
      <div ref={plotRef} className="cr-h-full cr-w-full" />
    </div>
  );
}
